/**
 * <size-helper>: "¿Qué talla soy?" on the product page (BACKLOG #43).
 *
 * Reads the measurements from the product's own size guide (the rich text
 * metafield custom.guia_de_tallas_2, already rendered in the page), so Cami
 * keeps one source of truth. The calculator only switches on when every size
 * of the product can be read reliably; otherwise the same panel shows the
 * guide as it is.
 *
 * Reading rules agreed with Cami (2026-10-10):
 *   "Contorno ..." / "Contorno busto"  -> full circumference
 *   "Ancho"                             -> laid flat, armpit to armpit (x 2)
 *   "Ancho total" on its own            -> ambiguous, not used
 *   "Cintura media: 78–86"              -> mid-rise: sits below the navel, so it is
 *                                          compared with the body halfway between
 *                                          waist and hip (Cami, 2026-10-10)
 *   "Contorno cintura: 68–72"           -> elastic waist range
 *   "Cadera"                            -> full hip
 *
 * The visitor's answers are kept in localStorage (brisato-fit) so the next
 * product shows "Tu talla" straight away. Nothing leaves the browser except
 * anonymous brisato_size_helper_* events (behavior.js -> GA4).
 */
(() => {
  if (customElements.get('size-helper')) return;

  const KEY = 'brisato-fit';
  const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || '{}') || {}; } catch (_) { return {}; } };
  const write = (v) => { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (_) { /* private mode */ } };
  const track = (name, data) => { try { if (window.brisatoTrack) window.brisatoTrack(name, data); } catch (_) { /* never break */ } };
  const num = (v) => { const n = parseFloat(String(v || '').replace(',', '.')); return Number.isFinite(n) && n > 0 ? n : null; };
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const norm = (s) => String(s).toUpperCase().replace(/\s+/g, '').replace(/-/g, '/');
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Typical body measurements by usual size (cm). Spain / EU sizing.
  const BODY = {
    women: { chest: { XS: 82, S: 86, M: 92, L: 98, XL: 104, XXL: 110 }, waist: { XS: 64, S: 68, M: 74, L: 80, XL: 86, XXL: 92 }, hip: { XS: 88, S: 94, M: 100, L: 106, XL: 112, XXL: 118 } },
    men: { chest: { XS: 88, S: 94, M: 100, L: 106, XL: 112, XXL: 118 }, waist: { XS: 72, S: 78, M: 84, L: 90, XL: 96, XXL: 102 }, hip: { XS: 88, S: 94, M: 100, L: 104, XL: 110, XXL: 116 } },
  };
  // Ease (garment minus body, cm) each kind of garment aims for, by preference.
  const TARGET = {
    knit: { tight: 8, normal: 14, loose: 20 },
    jacket: { tight: 14, normal: 22, loose: 30 },
    shirt: { tight: 6, normal: 12, loose: 18 },
    dress: { tight: 0, normal: 4, loose: 8 },
    kimono: { tight: 4, normal: 10, loose: 20 },
    pants: { tight: 4, normal: 8, loose: 14 },
  };
  const OWN = { tight: -4, normal: 0, loose: 6 };

  // Trousers are asked by the number people buy (Spain): typical body waist / hip (cm).
  const PANT_NUMBERS = ['32', '34', '36', '38', '40', '42', '44', '46'];
  const BODY_NUM = {
    women: { waist: { 32: 58, 34: 62, 36: 66, 38: 70, 40: 74, 42: 78, 44: 82, 46: 86 }, hip: { 32: 84, 34: 88, 36: 92, 38: 96, 40: 100, 42: 104, 44: 108, 46: 112 } },
    men: { waist: { 32: 66, 34: 70, 36: 74, 38: 78, 40: 82, 42: 86, 44: 90, 46: 94 }, hip: { 32: 82, 34: 86, 36: 90, 38: 94, 40: 98, 42: 102, 44: 106, 46: 110 } },
  };
  // Cami's own equivalences (2026-11-02, msgs 491-492): with a usual number, these products
  // recommend this size straight away, even for a size the guide has no row for yet.
  const NUMBER_TO_SIZE = [
    { match: /bestias/i, map: { 32: 'XS', 34: 'XS', 36: 'S', 38: 'M', 40: 'M', 42: 'L' } },
    { match: /on[ií]rica/i, map: { 36: 'S', 38: 'S', 40: 'M', 42: 'M', 44: 'L', 46: 'L' } },
  ];

  /** The guide's text, turned into sizes with numbers. */
  function parseGuide(text) {
    const sizes = {};
    let cur = null;
    let ambiguous = false;
    const lines = String(text).split(/\n+/).map((l) => l.replace(/\s+/g, ' ').trim()).filter(Boolean);
    for (const line of lines) {
      const head = line.match(/^talla\s+([A-Za-z0-9/\- ]{1,8})\s*$/i);
      if (head) { cur = norm(head[1]); sizes[cur] = sizes[cur] || {}; continue; }
      if (!cur) continue;
      const m = line.match(/^(.+?)[:\s]+(\d+(?:[.,]\d+)?)(?:\s*[–-]\s*(\d+(?:[.,]\d+)?))?\s*(?:cm)?/i);
      if (!m) continue;
      const label = m[1].toLowerCase().replace(/[():]/g, ' ').replace(/\s+/g, ' ').trim();
      const a = num(m[2]);
      const b = m[3] ? num(m[3]) : null;
      const s = sizes[cur];
      if (label.includes('pierna') || label.includes('hombro') || label.includes('tiras') || label.includes('busto altura')) continue;
      if (label.includes('contorno') && label.includes('cintura')) { s.waist = [a, b || a]; continue; }
      if (label.includes('contorno') && !label.includes('cadera')) { s.chest = a; continue; }
      if (label === 'ancho' || label === 'ancho pecho') { s.chest = a * 2; continue; }
      if (label.startsWith('ancho total')) { ambiguous = true; continue; }
      if (label.includes('cintura') && label.includes('media')) { s.waist = [a, b || a]; s.mid = true; continue; }
      if (label.includes('cintura')) { s.waist = [a, b || a]; continue; }
      if (label.includes('cadera')) { s.hip = a; continue; }
      if (label.includes('manga')) { s.sleeve = a; continue; }
      if (label === 'largo' || label === 'largo total') { s.length = a; continue; }
    }
    return { sizes, ambiguous };
  }

  function kindOf(title) {
    const t = title.toLowerCase();
    if (/pantal/.test(t)) return 'pants';
    if (/vestido/.test(t)) return 'dress';
    if (/kimono/.test(t)) return 'kimono';
    if (/cazadora|chaqueta|jacket|abrigo/.test(t)) return 'jacket';
    if (/jersey|cardigan|sudadera|buso|punto/.test(t)) return 'knit';
    if (/camisa|blusa|top|camiseta/.test(t)) return 'shirt';
    return null;
  }

  class SizeHelper extends HTMLElement {
    connectedCallback() {
      this.cfg = JSON.parse(this.querySelector('[data-size-helper-json]').textContent);
      this.t = this.cfg.strings;
      this.page = this.closest('product-page') || document;
      this.guide = this.page.querySelector('.pp-size-guide');
      this.dialog = this.querySelector('dialog');
      this.fieldset = this.page.querySelector('[data-option][data-is-size="true"]');
      if (!this.guide || !this.dialog || !this.fieldset) return;

      this.radios = [...this.fieldset.querySelectorAll('input[type="radio"]')];
      this.options = this.radios.map((r) => r.dataset.value);
      this.kind = kindOf(this.cfg.title);
      this.parsed = parseGuide(this.guideText());
      this.data = this.options.map((o) => ({ name: o, ...(this.parsed.sizes[norm(o)] || {}) }));
      const fixed = this.kind === 'pants' && NUMBER_TO_SIZE.find((r) => r.match.test(this.cfg.title));
      this.numMap = fixed ? fixed.map : null;
      this.calc = this.isCalculable();

      const st = read();
      this.st = Object.assign({ fit: 'normal', adds: {} }, st);
      if (this.cfg.gender) this.st.gender = this.cfg.gender;
      this.tab = this.calc ? 'calc' : 'chart';

      // The existing "Guía de tallas" link opens this panel.
      this.toggle = this.page.querySelector('[data-size-guide-toggle]');
      if (this.toggle && this.calc) this.toggle.querySelector('span').textContent = this.t.open_cta;
      this.page.addEventListener('click', (e) => {
        if (e.target.closest('[data-size-guide-toggle], [data-size-helper-open]')) {
          e.preventDefault(); e.stopPropagation();
          const sheet = e.target.closest('dialog');
          if (sheet && sheet !== this.dialog) { sheet.close(); }
          this.open();
        }
      }, true);

      this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); });
      this.dialog.addEventListener('click', (e) => this.onClick(e));
      this.dialog.addEventListener('input', (e) => this.onInput(e));

      if (this.calc) {
        this.addToSheet();
        this.markRecommended();
      }
    }

    /**
     * The guide as lines. It is hidden, so innerText would lose the line
     * breaks: rebuild them from paragraphs, list items and <br>.
     */
    guideText() {
      const blocks = this.guide.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6');
      if (!blocks.length) return this.guide.textContent;
      const lines = [];
      blocks.forEach((el) => {
        if (el.querySelector('p, li')) return;   // only the innermost blocks
        const c = el.cloneNode(true);
        c.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
        lines.push(...c.textContent.split('\n'));
      });
      return lines.join('\n');
    }

    isCalculable() {
      if (!this.kind || this.parsed.ambiguous || !this.data.length) return false;
      if (this.kind === 'pants') {
        // A size with no row in the guide (a new XS, say) is fine when Cami's equivalences cover it.
        const read = this.data.filter((s) => s.waist || s.hip);
        if (read.length === this.data.length) return true;
        return !!this.numMap && read.length >= 2 && this.data.every((s) => s.waist || s.hip || Object.values(this.numMap).includes(s.name));
      }
      return this.data.every((s) => s.chest);
    }

    /* ---------- the calculation ---------- */
    usualKey() { return this.kind === 'pants' ? 'usualPants' : 'usualTop'; }

    compute() {
      if (!this.calc) return null;
      const st = this.st, g = BODY[st.gender || 'women'], T = TARGET[this.kind];
      const fit = st.fit || 'normal';
      const h = num(st.height);
      const usual = st[this.usualKey()];
      const rows = [];
      let ranked, basis, ease = null;

      if (this.kind === 'pants') {
        const gn = BODY_NUM[st.gender || 'women'];
        const waist = num(st.bodyWaist) || (usual ? gn.waist[usual] : null);
        const hip = num(st.bodyHip) || (usual ? gn.hip[usual] : null);
        if (!waist && !hip) return null;
        basis = num(st.bodyWaist) || num(st.bodyHip) ? 'body' : 'usual';
        const lean = { tight: -1, normal: 0, loose: 1 }[fit];
        // Mid-rise trousers sit below the navel: the body there is between waist and hip.
        const at = (s) => (s.mid && waist && hip ? (waist + hip) / 2 : waist);
        ranked = this.data.map((s, i) => [s, i]).filter(([s]) => s.waist || s.hip).map(([s, i]) => {
          let d = 0;
          if (s.waist && waist) {
            const [lo, hi] = s.waist;
            const waist = at(s);
            // Elastic or drawstring: a smaller waist cinches in, a bigger one does not fit.
            d += waist < lo ? (lo - waist) * 0.5 : waist > hi ? (waist - hi) * 3 : 0;
          }
          if (s.hip && hip) d += Math.abs((s.hip - hip) - T[fit]);
          return { s, d: d - lean * i * 0.01 };
        }).sort((a, b) => a.d - b.d);
        // Cami's equivalence wins when the visitor gave their usual number.
        const fixed = basis === 'usual' && this.numMap ? this.data.find((s) => s.name === this.numMap[usual]) : null;
        if (fixed) ranked = [{ s: fixed, d: -99 }, ...ranked.filter((r) => r.s !== fixed)];
        const best = ranked[0].s;
        if (best.waist && waist) {
          const [lo, hi] = best.waist;
          const waist = at(best);
          const w = waist < lo ? [this.t.waist_cinch, 2] : waist > hi ? [this.t.waist_tight, 1] : [this.t.waist_in, 2];
          rows.push({ key: 'waist', name: best.mid ? this.t.m_midwaist : this.t.m_waist, say: w[0], pos: w[1] });
        }
        if (best.hip && hip) { ease = best.hip - hip; rows.push(this.easeRow('hip', this.t.m_hip, ease, T)); }
        if (h && best.length) {
          const r = best.length / h;
          const w = r < 0.52 ? [this.t.pants_short, 1] : r <= 0.58 ? [this.t.pants_ankle, 2] : [this.t.pants_long, 3];
          rows.push({ key: 'length', name: this.t.m_length, say: w[0], pos: w[1] });
        }
      } else {
        const own = this.kind !== 'kimono' ? num(st.ownWidth) : null;
        const body = num(st.bodyChest);
        let score;
        if (own) { basis = 'own'; score = (s) => Math.abs((s.chest - 2 * own) - OWN[fit]); }
        else if (body) { basis = 'body'; score = (s) => Math.abs((s.chest - body) - T[fit]); }
        else if (usual) { basis = 'usual'; score = (s) => Math.abs((s.chest - g.chest[usual]) - T[fit]); }
        else return null;
        ranked = this.data.map((s) => ({ s, d: score(s) })).sort((a, b) => a.d - b.d);
        const best = ranked[0].s;
        if (basis === 'own') {
          const d = best.chest - 2 * own;
          const w = d < -3 ? [this.t.own_tighter, 1] : d <= 3 ? [this.t.own_same, 2] : d <= 9 ? [this.t.own_looser, 3] : [this.t.own_much, 4];
          rows.push({ key: 'chest', name: this.t.m_chest, say: w[0] + ' · ' + this.t.own_flat.replace('%n%', (d >= 0 ? '+' : '') + Math.round(d / 2)), pos: w[1] });
        } else {
          ease = best.chest - (basis === 'body' ? body : g.chest[usual]);
          rows.push(this.easeRow('chest', this.t.m_chest, ease, T));
        }
        const ownL = num(st.ownLength);
        if (best.length && ownL && basis === 'own') {
          const d = Math.round(best.length - ownL);
          const say = Math.abs(d) <= 2 ? this.t.own_len_same : (d > 0 ? this.t.own_len_longer : this.t.own_len_shorter).replace('%n%', Math.abs(d));
          rows.push({ key: 'length', name: this.t.m_length, say, pos: Math.abs(d) <= 2 ? 2 : d > 0 ? 3 : 1 });
        } else if (best.length && h) {
          const r = best.length / h;
          const w = this.kind === 'dress'
            ? (r < 0.52 ? [this.t.dress_above, 1] : r <= 0.62 ? [this.t.dress_below, 2] : [this.t.dress_long, 3])
            : (r < 0.29 ? [this.t.len_crop, 0] : r < 0.355 ? [this.t.len_waist, 1] : r <= 0.395 ? [this.t.len_hip, 2] : [this.t.len_below, 3]);
          rows.push({ key: 'length', name: this.t.m_length, say: w[0], pos: w[1] });
        }
        if (best.sleeve && best.sleeve >= 55 && h) {
          const d = best.sleeve - 0.395 * h;
          const w = d < -3 ? [this.t.sl_short, 1] : d <= 3 ? [this.t.sl_ok, 2] : [this.t.sl_long, 3];
          rows.push({ key: 'sleeve', name: this.t.m_sleeve, say: w[0], pos: w[1] });
        }
      }

      const best = ranked[0].s, alt = ranked[1] ? ranked[1].s : null;
      const notes = [];
      if (alt && Math.abs(ranked[1].d - ranked[0].d) <= 2) {
        const bigger = this.options.indexOf(alt.name) > this.options.indexOf(best.name);
        notes.push(['', this.t.between.replace('%dir%', bigger ? this.t.dir_loose : this.t.dir_tight).replace('%size%', alt.name)]);
      }
      if (ease !== null && ease < T.normal - 10) notes.push(['warn', this.t.too_small.replace('%size%', best.name)]);
      if (ease !== null && ease > T.normal + 12 && best.name === this.options[0]) notes.push(['', this.t.too_big]);
      const v = this.variantFor(best.name);
      if (v && !v.available) notes.push(['', this.t.sold_out.replace('%size%', best.name)]);
      else if (v && v.qty !== null && v.qty > 0 && v.qty <= 2) {
        notes.push(['', (v.qty === 1 ? this.t.low_one : this.t.low_many.replace('%n%', v.qty)).replace('%size%', best.name)]);
      }
      const extra = this.kind === 'pants' ? (num(st.bodyWaist) || num(st.bodyHip) ? 1 : 0) : (num(st.ownWidth) || num(st.bodyChest) ? 1 : 0);
      const precision = Math.min(4, 1 + (h ? 1 : 0) + extra + (basis === 'own' && num(st.ownLength) ? 1 : 0));
      const fitWord = basis === 'own' ? this.t['fit_own_' + fit] : this.t['fit_' + fit];
      return { best, rows, notes, precision, basis, fitWord };
    }

    easeRow(key, name, ease, T) {
      const d = ease - T.normal;
      const w = d < -10 ? [this.t.w_very_tight, 0] : d < -4 ? [this.t.w_tight, 1] : d <= 4 ? [this.t.w_normal, 2] : d <= 10 ? [this.t.w_loose, 3] : [this.t.w_very_loose, 4];
      return { key, name, say: w[0], pos: w[1] };
    }

    variantFor(size) {
      try {
        const json = JSON.parse(this.page.querySelector('[data-product-json]').textContent);
        const idx = Number(this.fieldset.dataset.optionIndex || 0);
        return json.variants.find((v) => v.options[idx] === size && v.available) || json.variants.find((v) => v.options[idx] === size) || null;
      } catch (_) { return null; }
    }

    /* ---------- panel ---------- */
    open() {
      if (this.dialog.open) return;
      this.render();
      this.dialog.showModal();
      document.documentElement.classList.add('sh-lock');
      requestAnimationFrame(() => requestAnimationFrame(() => this.dialog.classList.add('is-open')));
      this.moveBar(false);
      track('size_helper_open', { product: this.cfg.handle, calc: this.calc });
    }

    close() {
      if (!this.dialog.open) return;
      this.dialog.classList.remove('is-open');
      document.documentElement.classList.remove('sh-lock');
      const done = () => { this.dialog.close(); };
      if (reduce()) done(); else setTimeout(done, 280);
    }

    tabsHTML() {
      if (!this.calc) return '';
      return `<div class="sh-tabs" role="tablist">
        <button type="button" class="sh-tab" role="tab" data-tab="calc" aria-selected="${this.tab === 'calc'}">${esc(this.t.tab_calc)}</button>
        <button type="button" class="sh-tab" role="tab" data-tab="chart" aria-selected="${this.tab === 'chart'}">${esc(this.t.tab_chart)}</button>
        <span class="sh-tabs__bar"></span></div>`;
    }

    chips(attr, list, current) {
      return list.map(([k, l]) => `<button type="button" class="sh-chip" data-${attr}="${k}" aria-pressed="${current === k}">${esc(l)}</button>`).join('');
    }

    addBlock(key, title, sub, inner) {
      const open = !!(this.st.adds || {})[key];
      return `<div class="sh-add${open ? ' is-open' : ''}" data-add="${key}">
        <button type="button" class="sh-add__btn" data-act="toggle" aria-expanded="${open}"><span>${esc(title)}<small>${esc(sub)}</small></span><span class="sh-plus" aria-hidden="true">+</span></button>
        <div class="sh-collapse${open ? ' is-open' : ''}"><div class="sh-collapse__in"><div class="sh-add__body">${inner}</div></div></div></div>`;
    }

    field(id, label, value, ph) {
      return `<label class="sh-field">${esc(label)}<span class="sh-field__row"><input id="sh-${id}" data-field="${id}" inputmode="decimal" autocomplete="off" placeholder="${ph}" value="${esc(value || '')}"><span class="sh-field__unit">cm</span></span></label>`;
    }

    calcHTML() {
      const t = this.t, st = this.st, pants = this.kind === 'pants';
      const sizes = (pants ? (this.numMap ? PANT_NUMBERS.filter((n) => this.numMap[n]) : PANT_NUMBERS) :['XS', 'S', 'M', 'L', 'XL', 'XXL']).map((k) => [k, k]);
      let h = '';
      if (!this.cfg.gender) {
        h += `<div class="sh-q"><p class="sh-q__title">${esc(t.gender_q)}</p><div class="sh-chips sh-chips--2">${this.chips('gender', [['women', t.women], ['men', t.men]], st.gender || 'women')}</div></div>`;
      }
      h += `<div class="sh-q"><p class="sh-q__title">${esc(pants ? t.usual_bottom : t.usual_top)}</p><div class="sh-chips sh-chips--usual">${this.chips('usual', sizes, st[this.usualKey()])}</div></div>`;
      h += `<div class="sh-q"><p class="sh-q__title">${esc(t.fit_q)}</p><div class="sh-chips sh-chips--3">${this.chips('fit', [['tight', t.fit_tight_label], ['normal', t.fit_normal_label], ['loose', t.fit_loose_label]], st.fit || 'normal')}</div></div>`;
      h += `<div class="sh-collapse" data-result-wrap><div class="sh-collapse__in"><div class="sh-result" aria-live="polite">
        <div class="sh-result__top"><div><div class="sh-result__k">${esc(t.your_size)}</div><div class="sh-result__size" data-r-size></div></div><div class="sh-result__line" data-r-line></div></div>
        <div class="sh-result__body"><div class="sh-rows" data-r-rows></div>
          <div class="sh-legend"><span>${esc(t.legend_tight)}</span><span>${esc(t.legend_mid)}</span><span>${esc(t.legend_loose)}</span></div>
          <div class="sh-notes" data-r-notes></div>
          <div class="sh-precision"><span class="sh-precision__bar" data-r-bar><i></i><i></i><i></i><i></i></span><span data-r-prec></span></div>
        </div></div></div></div>`;
      h += `<div class="sh-more"><p class="sh-more__intro">${esc(t.refine_intro)}</p>`;
      h += this.addBlock('h', t.add_height, t.add_height_sub, this.field('height', t.height_label, st.height, '170'));
      if (!pants && this.kind !== 'kimono') {
        h += this.addBlock('o', t.add_own, t.add_own_sub,
          `<div class="sh-howto">${this.ownSVG()}<span>${esc(t.own_howto)}</span></div>` +
          this.field('ownWidth', t.own_width, st.ownWidth, '52') + this.field('ownLength', t.own_length, st.ownLength, '62'));
      }
      h += this.addBlock('b', t.add_body, pants ? t.add_body_sub_bottom : t.add_body_sub_top,
        pants ? this.field('bodyWaist', t.body_waist, st.bodyWaist, '74') + this.field('bodyHip', t.body_hip, st.bodyHip, '100')
              : this.field('bodyChest', t.body_chest, st.bodyChest, '92'));
      h += '</div>';
      return h;
    }

    ownSVG() {
      return '<svg viewBox="0 0 120 100" aria-hidden="true"><path d="M38 10 L52 6 Q60 12 68 6 L82 10 L108 30 L98 44 L88 38 L88 92 L32 92 L32 38 L22 44 L12 30 Z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
        '<line x1="33" y1="44" x2="87" y2="44" class="sh-svg-chest" stroke-width="3"/><circle cx="33" cy="44" r="2.5" class="sh-svg-chest-dot"/><circle cx="87" cy="44" r="2.5" class="sh-svg-chest-dot"/></svg>';
    }

    chartHTML() {
      return `<div class="sh-chart rte">${this.guide.innerHTML.trim()}</div><p class="sh-chart__note">${esc(this.t.chart_note)}</p>`;
    }

    render() {
      const body = this.dialog.querySelector('[data-sh-body]');
      this.dialog.querySelector('[data-sh-tabs]').innerHTML = this.tabsHTML();
      body.innerHTML = `<div class="sh-panel" data-sh-panel>${this.tab === 'calc' ? this.calcHTML() : this.chartHTML()}</div>`;
      this.updateResult(true);
    }

    moveBar(animate) {
      const bar = this.dialog.querySelector('.sh-tabs__bar');
      const sel = this.dialog.querySelector('.sh-tab[aria-selected="true"]');
      if (!bar || !sel) return;
      if (!animate) bar.style.transition = 'none';
      bar.style.width = `${sel.offsetWidth}px`;
      bar.style.transform = `translateX(${sel.offsetLeft}px)`;
      if (!animate) { void bar.offsetWidth; bar.style.transition = ''; }
    }

    updateResult(first) {
      const foot = this.dialog.querySelector('[data-sh-foot]');
      const wrap = this.dialog.querySelector('[data-result-wrap]');
      const r = this.tab === 'calc' ? this.compute() : null;
      this.result = r;
      foot.innerHTML = r ? `<button type="button" class="pp-btn" data-act="choose">${esc(this.t.choose.replace('%size%', r.best.name))}</button>` : '';
      foot.hidden = !r;
      if (!wrap) return;
      if (!r) { wrap.classList.remove('is-open'); this.lastSize = null; return; }
      const size = wrap.querySelector('[data-r-size]');
      if (size.textContent !== r.best.name) {
        size.textContent = r.best.name;
        if (!first && this.lastSize) { size.classList.remove('is-pop'); void size.offsetWidth; size.classList.add('is-pop'); }
      }
      this.lastSize = r.best.name;
      wrap.querySelector('[data-r-line]').textContent = this.t.fits.replace('%fit%', r.fitWord);
      const rowsEl = wrap.querySelector('[data-r-rows]');
      const want = r.rows.map((m) => m.key);
      [...rowsEl.children].forEach((el) => { if (!want.includes(el.dataset.key)) el.remove(); });
      r.rows.forEach((m, i) => {
        let el = rowsEl.querySelector(`[data-key="${m.key}"]`);
        if (!el) {
          el = document.createElement('div');
          el.className = `sh-m${first ? '' : ' is-new'}`;
          el.dataset.key = m.key;
          el.innerHTML = `<div class="sh-m__row"><span class="sh-m__name"><i class="sh-dot sh-dot--${m.key}"></i>${esc(m.name)}</span><span class="sh-m__say"></span></div>
            <div class="sh-scale"><i></i><i></i><i class="is-mid"></i><i></i><i></i><b style="left:50%"></b></div>`;
          rowsEl.insertBefore(el, rowsEl.children[i] || null);
        }
        el.querySelector('.sh-m__say').textContent = m.say;
        const mark = el.querySelector('b');
        requestAnimationFrame(() => { mark.style.left = `${m.pos * 20 + 10}%`; });
      });
      const notesEl = wrap.querySelector('[data-r-notes]');
      const key = JSON.stringify(r.notes);
      if (notesEl.dataset.key !== key) {
        notesEl.dataset.key = key;
        notesEl.innerHTML = r.notes.map(([k, n]) => `<div class="sh-note${k ? ' sh-note--' + k : ''}">${esc(n)}</div>`).join('');
      }
      [...wrap.querySelector('[data-r-bar]').children].forEach((i, n) => i.classList.toggle('is-on', n < r.precision));
      wrap.querySelector('[data-r-prec]').textContent = this.t['precision_' + r.precision];
      wrap.classList.add('is-open');
      if (!first) track('size_helper_result', { product: this.cfg.handle, size: r.best.name, basis: r.basis, precision: r.precision });
    }

    save() {
      const { adds, ...keep } = this.st;
      write(Object.assign(read(), keep));
    }

    onInput(e) {
      const f = e.target.dataset.field;
      if (!f) return;
      this.st[f] = e.target.value;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => { this.save(); this.updateResult(false); }, 300);
    }

    onClick(e) {
      if (e.target === this.dialog) { this.close(); return; }
      const b = e.target.closest('button');
      if (!b) return;
      if (b.dataset.act === 'close') { this.close(); return; }
      if (b.dataset.tab) { this.swapTab(b.dataset.tab); return; }
      const pick = (attr, key) => {
        this.st[key] = b.dataset[attr];
        b.parentElement.querySelectorAll('.sh-chip').forEach((c) => c.setAttribute('aria-pressed', String(c === b)));
        this.save(); this.updateResult(false);
      };
      if (b.dataset.usual) return pick('usual', this.usualKey());
      if (b.dataset.fit) return pick('fit', 'fit');
      if (b.dataset.gender) return pick('gender', 'gender');
      if (b.dataset.act === 'toggle') {
        const box = b.closest('.sh-add'), key = box.dataset.add;
        this.st.adds = this.st.adds || {};
        const open = !this.st.adds[key];
        this.st.adds[key] = open;
        box.classList.toggle('is-open', open);
        box.querySelector('.sh-collapse').classList.toggle('is-open', open);
        b.setAttribute('aria-expanded', String(open));
        if (open) setTimeout(() => { const i = box.querySelector('input'); if (i) i.focus({ preventScroll: true }); box.scrollIntoView({ behavior: reduce() ? 'auto' : 'smooth', block: 'nearest' }); }, 260);
        return;
      }
      if (b.dataset.act === 'choose' && this.result) {
        const size = this.result.best.name;
        const radio = this.radios.find((r) => r.dataset.value === size);
        if (radio && !radio.checked) radio.click();
        this.st.lastWhy = this.result.basis;
        this.save();
        track('size_helper_choose', { product: this.cfg.handle, size, basis: this.result.basis });
        this.close();
        setTimeout(() => this.markRecommended(), 300);
      }
    }

    swapTab(tab) {
      if (tab === this.tab) return;
      this.tab = tab;
      this.dialog.querySelectorAll('.sh-tab').forEach((x) => x.setAttribute('aria-selected', String(x.dataset.tab === tab)));
      this.moveBar(true);
      const panel = this.dialog.querySelector('[data-sh-panel]');
      panel.classList.add('is-swapping');
      setTimeout(() => {
        panel.innerHTML = tab === 'calc' ? this.calcHTML() : this.chartHTML();
        this.updateResult(true);
        requestAnimationFrame(() => panel.classList.remove('is-swapping'));
      }, reduce() ? 0 : 160);
    }

    /** "Tu talla" on the size button and a "Para ti" line, from what this browser already told us. */
    markRecommended() {
      const r = this.compute();
      this.fieldset.querySelectorAll('.sh-tag').forEach((x) => x.remove());
      let line = this.fieldset.parentElement.querySelector('.sh-forme');
      if (!r) { if (line) line.classList.remove('is-open'); return; }
      const radio = this.radios.find((x) => x.dataset.value === r.best.name);
      const label = radio && this.fieldset.querySelector(`label[for="${radio.id}"]`);
      if (label) label.insertAdjacentHTML('afterbegin', `<span class="sh-tag" aria-hidden="true">${esc(this.t.tag)}</span>`);
      if (!line) {
        line = document.createElement('div');
        line.className = 'sh-collapse sh-forme';
        line.innerHTML = '<div class="sh-collapse__in"><div class="sh-forme__box"><span data-forme></span><button type="button" class="pp-link" data-size-helper-open></button></div></div>';
        this.fieldset.insertAdjacentElement('afterend', line);
      }
      const why = { usual: this.t.why_usual, body: this.t.why_body, own: this.t.why_own }[r.basis];
      line.querySelector('[data-forme]').innerHTML = `${esc(this.t.for_you)} <b>${esc(this.t.size_word)} ${esc(r.best.name)}</b> · ${esc(why)}`;
      line.querySelector('[data-size-helper-open]').textContent = this.t.change;
      requestAnimationFrame(() => requestAnimationFrame(() => line.classList.add('is-open')));
    }

    /** The mobile "Elige tu talla" sheet gets the same entry point. */
    addToSheet() {
      const sheetGuide = this.page.querySelector('.pp-sheet__guide');
      if (!sheetGuide || sheetGuide.previousElementSibling?.matches('.sh-sheet-link')) return;
      sheetGuide.insertAdjacentHTML('beforebegin', `<button type="button" class="pp-link sh-sheet-link" data-size-helper-open>${esc(this.t.open_cta)}</button>`);
      sheetGuide.hidden = true;
    }
  }

  customElements.define('size-helper', SizeHelper);
})();
